import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Dropdown, Spinner } from 'react-bootstrap';
import { EllipsisV } from '@styled-icons/fa-solid';
import DataTable, { useDataTableHandle } from '../../DataTable';
import { getMasterDataCountry, upsertMasterDataCountry, getMasterDataEpoAreaCode } from '../../apiCalls';
import CountryModal from './countryModal';
import ConfirmationModal from '../../ConfirmationModal';
import ToastNotification from '../../Toast/ToastNotification';
import { RefDataContext } from '../Maintenance';

const Country = () => {
  const [country, setCountry] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastConfig, setToastConfig] = useState({ type: 'success', message: '' });
  const [areaCodes, setAreaCodes] = useState([]);
  const [showConfirmDelete, setShowConfirmDelete] = useState(null);
  const [paginationLoaded, setPaginationLoaded] = useState(false);

  const dth = useDataTableHandle();
  const { registerAddHandler } = useContext(RefDataContext);

  const updateData = useCallback(async (setPaginationLength, pagination) => {
    try {
      setLoading(true);
      const pageNumber = pagination.pageNo + 1;
      const response = await getMasterDataCountry(pageNumber, pagination.itemsPerPage, 0);
      const data = response?.data ?? (Array.isArray(response) ? response : []);
      const totalItems = response?.totalCount ?? response?.totalItems ?? data.length;
      setCountry(data);
      setPaginationLength(totalItems);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshCountry = useCallback(() => {
    if (!dth?.current?.pagination?.itemsPerPage) return;
    updateData(dth.current.setServerDataLength, dth.current.pagination);
  }, [updateData, dth]);

  useEffect(() => {
    const fetchAreaCodes = async () => {
      try {
        const response = await getMasterDataEpoAreaCode(1, 1000, 0);
        setAreaCodes(response.data || []);
      } catch (err) {
        console.error('Failed to fetch area codes:', err);
      }
    };
    fetchAreaCodes();
    registerAddHandler('country', () => setSelectedCountry({ id: -1 }));
  }, [registerAddHandler]);

  useEffect(() => {
    if (!paginationLoaded || !dth?.current?.pagination?.itemsPerPage) return;
    updateData(dth.current.setServerDataLength, dth.current.pagination);
  }, [paginationLoaded, updateData]);

  const handleDelete = async (row) => {
    try {
      let areaCodeId = '';
      if (row.areaCode && areaCodes.length > 0) {
        const matchedCode = areaCodes.find((ac) => ac.areacode === row.areaCode);
        if (matchedCode) areaCodeId = matchedCode.id;
      }
      await upsertMasterDataCountry({
        id: row.id,
        country: row.country,
        abbreviation: row.abbreviation || '',
        areaCode: areaCodeId || '',
        softDelete: 1,
      });
      setShowConfirmDelete(null);
      setToastConfig({ type: 'success', message: 'Country deleted successfully!' });
      setShowToast(true);
      refreshCountry();
    } catch (err) {
      setToastConfig({ type: 'error', message: err.message || 'Failed to delete country' });
      setShowToast(true);
    }
  };

  return (
    <>
      <ToastNotification
        show={showToast}
        onClose={() => setShowToast(false)}
        type={toastConfig.type}
        message={toastConfig.message}
      />

      <CountryModal
        country={selectedCountry}
        onSave={(isNew) => {
          setToastConfig({
            type: 'success',
            message: isNew ? 'Country added successfully!' : 'Country updated successfully!',
          });
          setShowToast(true);
          setSelectedCountry(null);
          refreshCountry();
        }}
        onError={(error) => {
          setToastConfig({ type: 'error', message: error || 'Failed to save country' });
          setShowToast(true);
        }}
        onCancel={() => setSelectedCountry(null)}
      />

      {loading && (
        <span className="d-flex align-items-center gap-2 mb-2">
          <Spinner animation="border" size="sm" /> Loading...
        </span>
      )}

      <DataTable
        idKey="id"
        handle={dth}
        data={country}
        className="table-striped"
        lazyLoading
        onUpdate={updateData}
        onPaginationLoaded={() => setPaginationLoaded(true)}
        isLoading={loading}
      >
        <DataTable.Headers
          style={{
            fontSize: '14.4px',
            backgroundColor: '#fff',
            paddingBottom: '10px',
            borderBottom: '1px solid #ddd',
            borderTop: '1px solid #ddd',
          }}
        />
        <DataTable.Pagination itemsPerPage={[10, 25, 50, 100]} className="w-100 d-flex justify-content-end" />
        <DataTable.Row style={{ fontSize: '15px' }} />
        <DataTable.Column colKey="country" header="Country" />
        <DataTable.Column colKey="abbreviation" header="Abbreviation" />
        <DataTable.Column colKey="areaCode" header="Area Code" />
        <DataTable.Column
          colKey="actions"
          header=""
          hideFilter
          children={(item) => (
            <Dropdown className="w-100 h-100 d-grid center">
              <Dropdown.Toggle as="button" className="dot-menu-button">
                <EllipsisV width={'0.5rem'} />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item as="button" onClick={() => setSelectedCountry(item)}>
                  View/Edit
                </Dropdown.Item>
                <Dropdown.Item as="button" onClick={() => setShowConfirmDelete(item)}>
                  Remove
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}
        />
      </DataTable>

      <ConfirmationModal
        header="Confirm Deletion"
        show={!!showConfirmDelete}
        onHide={() => setShowConfirmDelete(null)}
        onConfirm={() => showConfirmDelete && handleDelete(showConfirmDelete)}
      >
        Are you sure you want to delete this record?
      </ConfirmationModal>
    </>
  );
};

export default Country;
